import { useCallback, useEffect, useState } from 'react';
import { debounce } from '@gov-nx/utils/common';

export const useScrollPosition = () => {
	const [scrollY, setScrollY] = useState(0);
	const [direction, setDirection] = useState<'down' | 'up'>();

	const update = useCallback(() => {
		const scrollDirection = scrollY < window.scrollY ? 'down' : 'up';
		setScrollY(window.scrollY);
		setDirection(scrollDirection);
	}, [scrollY]);

	let animationFrame: number;
	useEffect(() => {
		const onScroll = () => {
			// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
			animationFrame = requestAnimationFrame(update);
		};

		window.addEventListener('scroll', onScroll, { passive: true });

		return () => {
			window.removeEventListener('scroll', onScroll);
			cancelAnimationFrame(animationFrame);
		};
	}, [scrollY]);

	return { scrollY, direction };
};

export const useCarouselScroll = () => {
	const [listElement, setListElement] = useState<HTMLUListElement | null>(null);

	const listRef = useCallback((node: HTMLUListElement) => {
		if (node !== null) {
			setListElement(node);
		}
	}, []);

	const childElWidth = listElement?.children[0]?.clientWidth;

	const padding = listElement
		? parseInt(
				window
					.getComputedStyle(listElement, null)
					.getPropertyValue('padding-left'),
				10
			)
		: 0;

	const [state, setState] = useState({
		isOnLeftEdge: true,
		isOnRightEdge: false,
	});

	const onLeft = () => {
		listElement?.scrollBy({
			left: -(childElWidth ?? listElement.clientWidth),
			behavior: 'smooth',
		});
	};

	const onRight = () => {
		listElement?.scrollBy({
			left: childElWidth ?? listElement.clientWidth,
			behavior: 'smooth',
		});
	};

	const checkScrollPosition = useCallback(() => {
		if (!listElement) return;

		const { scrollLeft, scrollWidth, clientWidth } = listElement;
		setState({
			isOnLeftEdge: scrollLeft <= padding,
			isOnRightEdge: scrollLeft + clientWidth >= scrollWidth - padding,
		});
	}, [listElement, padding]);

	useEffect(() => {
		const check = debounce(checkScrollPosition, 50);

		listElement?.addEventListener('scroll', check);
		return () => {
			listElement?.removeEventListener('scroll', check);
		};
	}, [listElement, checkScrollPosition]);

	useEffect(() => {
		checkScrollPosition();
	}, [checkScrollPosition]);

	return {
		ref: listRef,
		onLeft,
		onRight,
		isOnLeftEdge: state.isOnLeftEdge,
		isOnRightEdge: state.isOnRightEdge,
	};
};
